import React, { type ReactNode } from "react";
import { Box, Grid } from "@mui/material";
import MuiSearchInput from "@/components/muiCustom/search/MuiSearchInput";
import RpAutocompleteWithApply from "@/components/muiCustom/select/RpAutocompleteWithApply";
import RpFieldLabel from "@/components/muiCustom/label/RpFieldLabel";
import LocationGroupFilter from "./LocationGroupFilter";

type FilterOption = {
  value: string;
  label: string;
  disabled?: boolean;
};

type LocationManagementFilterValues = {
  groupId?: string[];
  participantId?: string[];
  searchKey?: string;
};

type LocationManagementFiltersProps = {
  filters: LocationManagementFilterValues;
  onFilterChange: (
    _key: "groupId" | "participantId" | "searchKey",
    _value: string[] | string
  ) => void;
  groupSections?: Array<{
    id: string | number;
    type: string;
    groups: FilterOption[];
  }>;
  groupOptions?: FilterOption[];
  participantOptions?: FilterOption[];
  onParticipantListEndReached?: () => void;
  actionSlot?: ReactNode;
};

const FILTER_NO_DATA_MESSAGES = {
  participant: "No participants found.",
};

const LocationManagementFilters = ({
  filters,
  onFilterChange,
  groupSections = [],
  groupOptions = [],
  participantOptions = [],
  onParticipantListEndReached,
  actionSlot,
}: LocationManagementFiltersProps) => {
  const effectiveGroupSections = groupSections.length
    ? groupSections
    : [{ id: "groups", type: "Groups", groups: groupOptions }];

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 3 }}>
          <LocationGroupFilter
            groupSections={effectiveGroupSections}
            value={filters.groupId ?? []}
            onChange={(v) => onFilterChange("groupId", v)}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <RpFieldLabel>Participant</RpFieldLabel>
          <RpAutocompleteWithApply
            options={participantOptions}
            value={filters.participantId ?? []}
            onChange={(v) => onFilterChange("participantId", v)}
            placeholder="Participant"
            clearLabel="Clear Selection"
            noOptionsText={FILTER_NO_DATA_MESSAGES.participant}
            useVirtualList
            onListEndReached={onParticipantListEndReached}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Grid size={{ xs: 12, lg: actionSlot ? 8 : 12 }}>
          <MuiSearchInput
            value={filters.searchKey}
            onChange={(v) => onFilterChange("searchKey", v)}
            placeholder="Search"
            className="primary-selection-theme"
          />
        </Grid>
        {actionSlot ? (
          <Grid
            size={{ xs: 12, lg: 4 }}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: { xs: "stretch", lg: "flex-end" },
              gap: 1.5,
            }}
          >
            {actionSlot}
          </Grid>
        ) : null}
      </Grid>
    </Box>
  );
};

export default LocationManagementFilters;
